/* eslint-disable no-new,max-len,no-unused-expressions,no-nested-ternary */
/* global G, PLATFORM */
import { parseURL } from 'whatwg-url-without-unicode'
import { curry, isObj, PlatformEvent } from 'lib/util'
import { showBlockingDialog } from 'app/_shared/events/dialog'
import back from 'app/_shared/events/redirect/back'

declare const PLATFORM: string

/**
 * Shows a dialog informing the user that the scanned code is a URL and asks them whether they want
 * to open it.
 *
 * @param module  current module composition object
 * @param url     scanned url
 */
const showUrlDialog = async (module: Gaia.AppModule.Spec, url: string) => await showBlockingDialog(module, null, {
  ...(url.includes('tel') && {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.phone',
      defaultValue: 'Phone number',
    },
  }) || (url.includes('mailto') && {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.mail',
      defaultValue: 'Email Address',
    },
  }) || {
    title: {
      ns: 'common',
      key: 'dialog.scan.url.title.website',
      defaultValue: 'Website Address',
    },
  },
  text: {
    ns: 'common',
    key: 'dialog.scan.url.textMD',
    defaultValue: 'Scanned **{{url}}**.',
    md: true,
    url: url.replace(/^(tel|mailto):/g, ''),
  },
  ok: {
    ns: 'common',
    key: 'button.open',
    defaultValue: 'Open',
  },
})

/**
 * Shows a dialog informing the user that `code` cannot be recognized.
 *
 * @param module  current module
 * @param code    scanned value
 */
const showUnknownCodeDialog = async (module: Gaia.AppModule.Spec, code: string) => await showBlockingDialog(module, null, {
  title: {
    ns: 'common',
    key: `dialog.scan.unknownCode.title`,
    defaultValue: 'Unknown code',
  },
  text: {
    ns: 'common',
    key: `dialog.scan.unknownCode.textMD`,
    defaultValue: 'The code **{{code}}** cannot be recognised.',
    md: true,
    code,
  },
  ok: {
    ns: 'common',
    key: 'button.copy',
    defaultValue: 'Copy',
  },
})

/**
 * Shows a dialog informing the user that `code` cannot be recognized.
 *
 * @param module  current module
 * @param [nick]  current sensor's nick
 */
const showWrongCodeDialog = async (module: Gaia.AppModule.Spec, nick: string = 'current') => await showBlockingDialog(module, null, {
  title: {
    ns: 'apmGo',
    key: `dialog.wrongCodeError.title`,
    defaultValue: 'Wrong code',
  },
  text: {
    ns: 'apmGo',
    key: `dialog.wrongCodeError.textMD`,
    defaultValue: 'The scanned code is not valid for the {{nick}} device',
    md: true,
    nick,
  },
  ok: {
    ns: 'common',
    key: 'button.ok',
    defaultValue: 'Ok',
  },
  cancel: false,
})

/**
 * Redirects to the `sensorDetail` action in the `xEcoApm` module.
 *
 * @param module current module
 * @param code   scanned value
 */
const goBackWithCode = async (module: Gaia.AppModule.Spec, { code } : { code: string }) => {
  await back(module, null, new PlatformEvent('change', { code }))

  return true
}

/**
 * Process Scanned Code Event Handler
 *
 * Attempts to recognize a scanned code and display a dialog accordingly:
 * - If code is a URL, the dialog asks the user whether they want to open it.
 * - If code is a QR code, it attempts to parse the code string as JSON:
 *  - If code can be parsed:
 *    - If code is an object with serial and signature properties, it redirects to OpenManagement.
 *      - If serial is different from the current sensor's serial, shows an error dialog.
 *      - If the serial the same as the current sensor's one, calls {@link goBackWithCode}
 *  - If code cannot be parsed, it shows a dialog informing that the code cannot be recognized.
 *
 * If the result of displaying a dialog is `true` (i.e. the user has clicked the 'ok' option), in
 * most cases, this event handler returns an object to be handled by the component that called it.
 *
 * @param module              the current module composition object
 * @param component           the current action's main component
 * @param event               information about the event that triggered this handler
 * @param event.detail        event details
 * @param event.detail.type   the type of scanned code
 * @param event.detail.value  the scanned code
 */
const processScannedCode = async (
  module: Gaia.AppModule.Spec,
  component: Gaia.Component.Spec,
  event: Gaia.PlatformEvent,
) => {
  const { type = 'qr', value } = event.detail || {}
  const { sensor } = module[G.MODEL][G.CHILDREN]
  const { value: { serial, nick } } = sensor[G.CACHE]

  try {
    const url = PLATFORM === 'mobile'
      ? parseURL(value, {})
      : URL.parse(value)

    if (url) {
      return await showUrlDialog(module, value) && { link: value }
    }

    if (type === 'qr') {
      const valueObj = JSON.parse(value.replace(/\n/g, ''))
      const isCode = isObj(valueObj) && valueObj.serial && valueObj.signature

      if (!isCode) {
        return await showUnknownCodeDialog(module, value) && { clipboard: value }
      }

      if (valueObj.serial !== serial) {
        return await showWrongCodeDialog(module, nick) && {}
      }

      return await goBackWithCode(module, { code: valueObj }) && { disable: true }
    }
  } catch (e) {
    return await showUnknownCodeDialog(module, value) && { clipboard: value }
  }
}

export default curry(processScannedCode)
